import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import FolderPermissionBlock from '../components/FolderPermissionBlock';
import { getAgent, updateAgentNotes, updateAgent, type UserInfo, type MaterialActivity } from '@/api/agent';
import { getTree, type CategoryNode } from '@/api/materialCategory';
import { listMaterial, saveConfidentialAgents, type MaterialItem } from '@/api/materialInfo';

type DisplayState = 0 | 2 | 3;

function getDisplayState(user: UserInfo): DisplayState {
  if (user.state === 2) return 2;
  if (user.state === 3) return 3;
  if (user.expirydate && user.expirydate < Date.now()) return 3;
  return 0;
}

const statusColor: Record<number, string> = {
  0: 'bg-green-50 text-green-700 border-green-200',
  2: 'bg-gray-100 text-gray-500 border-gray-200',
  3: 'bg-red-50 text-red-600 border-red-200',
};

const statusLabel: Record<number, string> = {
  0: '启用中',
  2: '已禁用',
  3: '已过期',
};

function formatDate(timestamp?: number): string {
  if (!timestamp) return '—';
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AgentActivityPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getAgent(id);
        if (!cancelled) {
          setAgent(data);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError((e as Error).message || '获取代理商信息失败');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [confMaterials, setConfMaterials] = useState<MaterialItem[]>([]);
  const [confMaterialsLoading, setConfMaterialsLoading] = useState(true);
  const [allMaterialsCount, setAllMaterialsCount] = useState(0);

  // 加载机密和非机密资料列表
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [confResult, allResult] = await Promise.all([
          listMaterial({ confidential: 1, pageSize: 999 }),
          listMaterial({ pageSize: 1 }),
        ]);
        if (!cancelled) {
          setConfMaterials(confResult.list);
          setAllMaterialsCount(allResult.total);
          setConfMaterialsLoading(false);
        }
      } catch {
        if (!cancelled) setConfMaterialsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const normalMaterials = allMaterialsCount - confMaterials.length;
  const materialActivities: MaterialActivity[] = agent?.materialactivities ?? [];
  const totalPreviews = agent?.totalviews ?? 0;
  const totalDownloads = agent?.totaldownloads ?? 0;

  const [localFolders, setLocalFolders] = useState<string[] | undefined>();
  const [folderSaved, setFolderSaved] = useState(false);
  const [confGrants, setConfGrants] = useState<Record<string, boolean>>({});
  const [confSaved, setConfSaved] = useState(false);
  const [notesVal, setNotesVal] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'overview' | 'permissions' | 'files'>(() => {
    const tab = searchParams.get('tab');
    if (tab === 'files' || tab === 'permissions') return tab;
    return 'overview';
  });
  const [categoryTree, setCategoryTree] = useState<CategoryNode[]>([]);

  // 计算分类/型号总数
  const allSubCategoryCount = categoryTree.reduce((sum, p) => sum + p.subCategories.length, 0);
  const allModelCount = categoryTree.reduce(
    (sum, p) => sum + p.subCategories.reduce((s, sub) => s + sub.models.length, 0),
    0,
  );

  useEffect(() => {
    getTree().then(setCategoryTree).catch(() => {});
  }, []);

  // 数据加载后同步 notes 到本地状态
  useEffect(() => {
    if (agent) {
      setNotesVal(agent.notes ?? '');
      // 同步资料分类权限
      if (agent.modelIds && agent.modelIds.length > 0) {
        setLocalFolders(agent.modelIds);
      }
    }
  }, [agent]);

  // 初始化机密资料授权勾选
  useEffect(() => {
    if (!id || confMaterials.length === 0) return;
    const init: Record<string, boolean> = {};
    confMaterials.forEach((m) => {
      init[m.id] = (m.confidentialAgents ?? []).includes(id);
    });
    setConfGrants(init);
  }, [id, confMaterials]);

  const [savingFolders, setSavingFolders] = useState(false);
  const [savingConf, setSavingConf] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // 加载中
  if (loading) {
    return (
      <AdminLayout title="活动记录" subtitle="加载中...">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-loader-4-line text-4xl mb-3 animate-spin"></i>
          <p className="text-base font-semibold">正在加载代理商信息...</p>
        </div>
      </AdminLayout>
    );
  }

  // 加载失败
  if (error || !agent) {
    return (
      <AdminLayout title="活动记录" subtitle="未找到代理商">
        <div className="flex flex-col items-center justify-center py-32 text-gray-400">
          <i className="ri-user-search-line text-4xl mb-3"></i>
          <p className="text-base font-semibold">{error || '未找到该代理商'}</p>
          <button onClick={() => navigate('/admin/agents')} className="mt-4 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
            返回列表
          </button>
        </div>
      </AdminLayout>
    );
  }

  const ds = getDisplayState(agent);
  const isAllFolders = !localFolders || localFolders.length === 0;

  // 从分类树构建 分类ID→名称 映射
  const primaryLabelMap = new Map<string, string>();
  const subLabelMap = new Map<string, string>();
  for (const p of categoryTree) {
    primaryLabelMap.set(p.id, p.label);
    if (p.subCategories) {
      for (const sub of p.subCategories) {
        subLabelMap.set(sub.id, sub.label);
      }
    }
  }
  const grantedConfCount = Object.values(confGrants).filter(Boolean).length;
  const grantedConf = confMaterials.filter((m) => confGrants[m.id]);

  const handleSaveFolders = async () => {
    if (!agent) return;
    setSavingFolders(true);
    try {
      await updateAgent({
        id: agent.id,
        modelIds: localFolders && localFolders.length > 0 ? localFolders.join(',') : '',
      });
      setFolderSaved(true);
      setTimeout(() => setFolderSaved(false), 2500);
    } catch (e: unknown) {
      showToast((e as Error).message || '保存分类权限失败', 'error');
    } finally {
      setSavingFolders(false);
    }
  };

  const handleSaveConf = async () => {
    if (!id) return;
    setSavingConf(true);
    try {
      for (const mat of confMaterials) {
        const wasGranted = (mat.confidentialAgents ?? []).includes(id);
        const isGranted = confGrants[mat.id] ?? false;
        if (wasGranted === isGranted) continue;
        const currentAgents = mat.confidentialAgents ?? [];
        const updatedAgents = isGranted
          ? [...currentAgents, id]
          : currentAgents.filter((a) => a !== id);
        await saveConfidentialAgents(mat.id, updatedAgents);
      }
      setConfSaved(true);
      setTimeout(() => setConfSaved(false), 2500);
      showToast('机密资料授权已保存');
    } catch (e: unknown) {
      showToast((e as Error).message || '保存机密授权失败', 'error');
    } finally {
      setSavingConf(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!agent) return;
    setSavingNotes(true);
    try {
      await updateAgentNotes(agent.id, notesVal);
      setEditingNotes(false);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (e: unknown) {
      showToast((e as Error).message || '保存备注失败', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <AdminLayout
      title={`${agent.name} — 详情`}
      subtitle={`${agent.company ?? ''} · ${agent.region ?? ''}`}
    >
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-2 px-4 py-3 bg-white border rounded-xl text-sm font-semibold ${toast.type === 'error' ? 'border-red-200 text-red-700' : 'border-green-200 text-green-700'}`}
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
        >
          <i className={`${toast.type === 'error' ? 'ri-error-warning-fill text-red-500' : 'ri-checkbox-circle-fill text-green-500'} text-base`}></i>
          {toast.message}
        </div>
      )}

      {/* Back bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/admin/agents')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-base"></i>
          返回代理商列表
        </button>
        <button
          onClick={() => navigate(`/admin/agents/${id}/edit`)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
          style={{ background: '#0069BD' }}
        >
          <i className="ri-pencil-line text-sm"></i>
          编辑代理商
        </button>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Left sidebar: profile */}
        <div className="col-span-1 space-y-4">
          {/* Profile */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-bold mb-3" style={{ background: '#0069BD' }}>
                {(agent.name || '?')[0]}
              </div>
              <p className="text-base font-bold text-gray-900">{agent.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{agent.email ?? agent.loginname}</p>
              <span className={`inline-flex items-center mt-2 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusColor[ds]}`}>
                {statusLabel[ds]}
              </span>
            </div>
            <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-building-line text-xs w-4 text-center shrink-0"></i>
                <span className="truncate">{agent.company || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-map-pin-line text-xs w-4 text-center shrink-0"></i>
                <span>{agent.region || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-calendar-line text-xs w-4 text-center shrink-0"></i>
                <span>有效至 {formatDate(agent.expirydate)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-time-line text-xs w-4 text-center shrink-0"></i>
                <span>注册于 {formatDate(agent.createdate)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <i className="ri-history-line text-xs w-4 text-center shrink-0"></i>
                <span>最后登录 {formatDate(agent.lastlogindate)}</span>
              </div>
            </div>
          </div>

          {/* Quick stats */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">活动概览</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5"><i className="ri-eye-line text-xs"></i>总浏览量</span>
                <span className="text-sm font-bold text-gray-800">{totalPreviews.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5"><i className="ri-download-line text-xs"></i>总下载量</span>
                <span className="text-sm font-bold text-gray-800">{totalDownloads.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5"><i className="ri-file-list-line text-xs"></i>访问文件数</span>
                <span className="text-sm font-bold text-gray-800">{materialActivities.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5"><i className="ri-shield-keyhole-line text-xs"></i>机密授权</span>
                <span className="text-sm font-bold text-gray-800">{grantedConfCount} / {confMaterials.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5"><i className="ri-layout-grid-line text-xs"></i>分类权限</span>
                <span className="text-sm font-bold text-gray-800">
                  {isAllFolders ? `全部 ${allModelCount}` : `${localFolders?.length ?? 0} / ${allModelCount}`}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">备注</p>
              {!editingNotes && (
                <button
                  onClick={() => { setEditingNotes(true); setNotesSaved(false); }}
                  className="text-xs font-semibold cursor-pointer whitespace-nowrap"
                  style={{ color: '#0069BD' }}
                >
                  {notesVal ? '编辑' : '添加'}
                </button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notesVal}
                  onChange={(e) => setNotesVal(e.target.value)}
                  placeholder="填写备注..."
                  rows={4}
                  maxLength={500}
                  autoFocus
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-gray-400 resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{notesVal.length}/500</span>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingNotes(false); setNotesVal(agent.notes ?? ''); }} className="px-3 py-1.5 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                    <button onClick={handleSaveNotes} disabled={savingNotes} className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#0069BD' }}>{savingNotes ? '保存中...' : '保存'}</button>
                  </div>
                </div>
              </div>
            ) : notesVal ? (
              <div className="relative">
                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-lg px-3 py-2.5 whitespace-pre-wrap">{notesVal}</p>
                {notesSaved && <span className="absolute top-2 right-2 flex items-center gap-1 text-xs text-green-600 font-semibold"><i className="ri-checkbox-circle-line text-xs"></i>已保存</span>}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">暂无备注</p>
            )}
          </div>
        </div>

        {/* Right: tabs content */}
        <div className="col-span-3 space-y-5">
          {/* Tabs */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100 p-1.5">
            {[
              { key: 'overview', label: '资料权限', icon: 'ri-shield-line' },
              { key: 'files', label: '文件访问明细', icon: 'ri-file-list-line' },
              { key: 'permissions', label: '分类权限设置', icon: 'ri-layout-grid-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: overview (permissions summary) */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Access summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                  <p className="text-3xl font-bold text-gray-800">{normalMaterials}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1"><i className="ri-global-line text-xs"></i>普通资料</p>
                  <p className="text-xs text-gray-500 mt-1.5 font-medium">全部可见</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                  <p className="text-3xl font-bold text-gray-800">{grantedConfCount}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1"><i className="ri-shield-keyhole-line text-xs"></i>机密资料</p>
                  <p className="text-xs text-gray-400 mt-1.5">共 {confMaterials.length} 份</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-5 text-center">
                  <p className="text-3xl font-bold text-gray-800">{isAllFolders ? allModelCount : (localFolders?.length ?? 0)}</p>
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1"><i className="ri-layout-grid-line text-xs"></i>可见分类</p>
                  <p className="text-xs text-gray-400 mt-1.5">共 {allModelCount} 个</p>
                </div>
              </div>

              {/* Confidential materials list */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <i className="ri-shield-keyhole-line text-gray-400"></i>机密资料授权
                  </h3>
                  <div className="flex items-center gap-2">
                    {confSaved && <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><i className="ri-checkbox-circle-line text-xs"></i>已保存</span>}
                    <button onClick={handleSaveConf} disabled={savingConf} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#0069BD' }}>{savingConf ? '保存中...' : '保存权限'}</button>
                  </div>
                </div>
                {confMaterialsLoading ? (
                  <div className="py-8 text-center text-gray-400">
                    <i className="ri-loader-4-line text-2xl mb-2 block animate-spin"></i>
                    <p className="text-sm">加载机密资料...</p>
                  </div>
                ) : confMaterials.length === 0 ? (
                  <p className="text-sm text-gray-400 italic text-center py-6">暂无机密资料</p>
                ) : (
                  <div className="space-y-2">
                    {confMaterials.map((mat) => {
                      const granted = confGrants[mat.id] ?? false;
                      const matPrimaryLabel = mat.primaryCategory ? primaryLabelMap.get(mat.primaryCategory) : undefined;
                      const matSubLabel = mat.category ? subLabelMap.get(mat.category) : undefined;
                      return (
                        <label
                          key={mat.id}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${granted ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-100/60'}`}
                        >
                          <input
                            type="checkbox"
                            checked={granted}
                            onChange={() => setConfGrants((prev) => ({ ...prev, [mat.id]: !prev[mat.id] }))}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            style={{ accentColor: '#0069BD' }}
                          />
                          <div className="w-12 h-8 rounded-lg overflow-hidden shrink-0 bg-gray-100">
                            <img src={mat.thumbnail} alt={mat.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{mat.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 bg-gray-100 rounded-full text-gray-500">
                                {matPrimaryLabel && matSubLabel ? `${matPrimaryLabel} > ${matSubLabel}` : matPrimaryLabel || matSubLabel || mat.category}
                              </span>
                              <span className={`text-xs font-medium ${mat.type === 'PDF' ? 'text-red-500' : mat.type === 'PPT' ? 'text-orange-500' : mat.type === 'Video' ? 'text-purple-600' : mat.type === 'Excel' ? 'text-green-600' : mat.type === 'Word' ? 'text-blue-600' : 'text-gray-400'}`}>
                                {mat.type}
                              </span>
                            </div>
                          </div>
                          {granted && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 shrink-0 whitespace-nowrap">已授权</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: files */}
          {activeTab === 'files' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-bold text-gray-700">文件访问明细</h3>
                <span className="text-xs text-gray-400">共访问 {materialActivities.length} 个文件</span>
              </div>
              {materialActivities.length === 0 ? (
                <div className="py-16 text-center text-gray-400">
                  <i className="ri-file-search-line text-4xl mb-3 block"></i>
                  <p className="text-sm font-medium">暂无访问记录</p>
                  <p className="text-xs mt-1">该代理商尚未访问任何资料</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {materialActivities.map((act) => {
                    const primaryLabel = act.primarycategory ? primaryLabelMap.get(act.primarycategory) : undefined;
                    const subLabel = act.category ? subLabelMap.get(act.category) : undefined;
                    const isVideo = act.isvideo === 1;
                    const canDownload = act.downloadallowed === 1;
                    return (
                      <div key={act.materialid} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                        {act.thumbnail ? (
                          <div className="w-14 h-10 rounded-lg overflow-hidden shrink-0 bg-gray-100 relative">
                            <img src={act.thumbnail} alt={act.title} className="w-full h-full object-cover object-top" />
                            {isVideo && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <i className="ri-play-fill text-white text-xs"></i>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={`w-14 h-10 rounded-lg flex items-center justify-center shrink-0 ${isVideo ? 'bg-violet-100' : act.type === 'PDF' ? 'bg-red-100' : 'bg-orange-100'}`}>
                            <i className={`text-base ${isVideo ? 'ri-video-line text-violet-600' : act.type === 'PDF' ? 'ri-file-pdf-line text-red-600' : 'ri-file-ppt-line text-orange-600'}`}></i>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => navigate(`/admin/materials/${act.materialid}`)}
                            className="text-sm font-semibold text-gray-800 truncate max-w-full text-left cursor-pointer hover:underline transition-colors block"
                          >
                            {act.title}
                          </button>
                          <div className="flex items-center gap-2 mt-0.5">
                            {(primaryLabel || subLabel) && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-500">
                                {primaryLabel && subLabel ? `${primaryLabel} > ${subLabel}` : primaryLabel || subLabel}
                              </span>
                            )}
                            <span className={`text-xs font-medium ${act.type === 'PDF' ? 'text-red-500' : act.type === 'PPT' ? 'text-orange-500' : act.type === 'Video' ? 'text-purple-600' : act.type === 'Excel' ? 'text-green-600' : act.type === 'Word' ? 'text-blue-600' : 'text-gray-400'}`}>
                              {act.type || '—'}
                            </span>
                            <span className="text-xs text-gray-400">{formatDate(act.lastaccesstime)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 shrink-0">
                          <div className="text-center">
                            <p className="text-base font-bold text-gray-800">{act.views.toLocaleString()}</p>
                            <p className="text-xs text-gray-400 flex items-center gap-0.5 justify-center"><i className="ri-eye-line text-xs"></i>浏览</p>
                          </div>
                          {canDownload ? (
                            <div className="text-center">
                              <p className="text-base font-bold text-gray-800">{act.downloads.toLocaleString()}</p>
                              <p className="text-xs text-gray-400 flex items-center gap-0.5 justify-center"><i className="ri-download-line text-xs"></i>下载</p>
                            </div>
                          ) : (
                            <div className="text-center opacity-40">
                              <p className="text-base font-bold text-gray-400">—</p>
                              <p className="text-xs text-gray-400 flex items-center gap-0.5 justify-center"><i className="ri-lock-line text-xs"></i>下载</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Tab: permissions */}
          {activeTab === 'permissions' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <i className="ri-layout-grid-line text-gray-400"></i>资料分类权限
                </h3>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    {isAllFolders ? `全部 ${allModelCount} 个分类可见` : `已限定 ${localFolders?.length ?? 0} / ${allModelCount} 个分类`}
                  </span>
                  <div className="flex items-center gap-2">
                    {folderSaved && <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><i className="ri-checkbox-circle-line text-xs"></i>已保存</span>}
                    <button onClick={handleSaveFolders} disabled={savingFolders} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: '#0069BD' }}>{savingFolders ? '保存中...' : '保存权限'}</button>
                  </div>
                </div>
              </div>
              <FolderPermissionBlock
                categoryTree={categoryTree}
                allowedModels={localFolders}
                onChange={setLocalFolders}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
